import { css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useEffect } from 'react';

import { useLoadingStore } from '@stores/useLoadingStore';
import { Spacing, TextInput } from 'chatie-design-system';
import { setCookie } from 'cookies-next';
import { signInWithEmailAndPassword } from 'firebase/auth';

import Button from '@components/common/Button';

import { useGlobalContext } from '@contexts/global';
import { firebaseAuth } from '@utils/firebase';
import { useForm } from '@utils/form';
import { useMe } from '@utils/hook';
import { isEnv, toParams } from '@utils/misc';
import { getRouterQuery } from '@utils/query';

const signInFormCSS = css`
  padding: ${Spacing.xxlarge}px;
  > * + * {
    margin-top: ${Spacing.medium}px;
  }

  .sign-utils {
    display: flex;
    margin-top: 48px;
    justify-content: center;
    margin-bottom: 8px;

    span {
      font-family: 'Apple SD Gothic Neo';
      font-style: normal;
      font-weight: 400;
      line-height: normal;
      font-size: 13px;
      color: #90949c;
    }
  }
`;

const EmailSignin = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const next = getRouterQuery(router.query.next) as string;
  const isDev = isEnv('dev');
  const { isCheming } = useGlobalContext();

  const { data: me } = useMe();

  const { setLoading } = useLoadingStore();

  const { values, onChange, submit, isValidated, isSubmitting } = useForm(
    {
      email: '',
      password: '',
    },
    {
      validator: (values) => {
        if (!values.email) {
          return [false, t('이메일을 입력해주세요.')];
        }

        if (!values.password) {
          return [false, t('비밀번호를 입력해주세요.')];
        }

        return [true, undefined];
      },
      onSubmit: (done) => {
        setLoading(true);
        signInWithEmailAndPassword(firebaseAuth, values.email, values.password)
          .then((userCredential) => {
            userCredential.user.getIdToken(true).then((token) => {
              const cookieOptions = {
                expires: new Date(Date.now() + 1000 * 60 * 60),
                domain: isEnv('dev')
                  ? undefined
                  : isCheming
                  ? 'cheming.ai'
                  : 'chatie.me',
                secure: !isDev,
                path: '/',
              };

              setCookie('firebaseIdToken', token, cookieOptions);
              router.replace(next ?? '/');
            });

            if (!userCredential.user.emailVerified) {
              alert(
                t(
                  '이메일 인증이 완료되지 않은 계정입니다.\n메일함을 확인해주세요.'
                )
              );
              return;
            }
          })
          .catch(() => {
            alert(
              t('이메일 또는 비밀번호가 일치하지 않습니다. 다시 시도해주세요.')
            );
          })
          .finally(() => {
            setLoading(false);
            done();
          });
      },
    }
  );

  useEffect(() => {
    if (me) {
      router.replace(next ?? '/');
    }
  }, [me]);

  return (
    <form
      css={signInFormCSS}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <TextInput
        name="email"
        value={values.email}
        placeholder={t('이메일')}
        autoComplete="off"
        onChange={onChange}
      />
      <TextInput
        type="password"
        name="password"
        value={values.password}
        placeholder={t('비밀번호(영문+숫자 8자 이상)')}
        onChange={onChange}
      />
      <Button text={t('로그인')} disabled={!isValidated || isSubmitting} />

      <div className="social-btn-wrap__small">
        <div className="sign-utils">
          <span
            onClick={() => {
              const params = toParams({ next });
              router.push(`/signup?${params}`);
            }}
          >
            회원가입
          </span>
          <span
            style={{
              margin: '0px 6px',
            }}
          >
            {' '}
            |{' '}
          </span>
          <span
            onClick={() => {
              const params = toParams({ next });
              router.push(`/recovery-user?${params}`);
            }}
          >
            <p> 계정 찾기</p>
          </span>
        </div>
      </div>
    </form>
  );
};

export default EmailSignin;
